import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Loader, AlertCircle, Briefcase, Pencil } from 'lucide-react';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { eventTeamApiService } from '../../services/eventTeamApi';
import type { EventProject, EventProjectStatus, EventTag } from '../../types/eventTeam';
import { EVENT_PROJECT_STATUSES } from '../../types/eventTeam';
import { cn } from '../../utils/cn';

export interface EventProjectModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  project?: EventProject | null;
  onClose: () => void;
  onSuccess: (project: EventProject) => void;
}

function formatLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function msToLocalInput(ms: number | null | undefined): string {
  if (ms == null) return '';
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** Create/edit modal for event projects (guide §1.6). */
export function EventProjectModal({ isOpen, mode, project, onClose, onSuccess }: EventProjectModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(modalRef, onClose);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<EventProjectStatus>('planning');
  const [priority, setPriority] = useState(5);
  const [category, setCategory] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [tags, setTags] = useState<EventTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setSelectedTagIds([]);
    if (mode === 'edit' && project) {
      setTitle(project.title);
      setDescription(project.description);
      setStatus(project.status);
      setPriority(project.priority);
      setCategory(project.category ?? '');
      setDueDate(msToLocalInput(project.due_date));
    } else {
      setTitle('');
      setDescription('');
      setStatus('planning');
      setPriority(5);
      setCategory('');
      setDueDate('');
    }
    if (mode === 'create') {
      eventTeamApiService.listTags().then(setTags).catch(() => setTags([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      if (mode === 'create') {
        const created = await eventTeamApiService.createProject({
          title: title.trim(),
          description: description.trim(),
          status,
          priority,
          category: category.trim() || null,
          due_date: localInputToMs(dueDate),
        });
        // Tags are applied through the documented tag endpoints after creation.
        if (selectedTagIds.length > 0) {
          await Promise.allSettled(
            selectedTagIds.map((tagId) => eventTeamApiService.addTagToTarget('project', created.id, tagId)),
          );
        }
        onSuccess(created);
      } else if (project) {
        const updated = await eventTeamApiService.updateProject(project.id, {
          title: title.trim(),
          description: description.trim(),
          priority,
          category: category.trim() || null,
          due_date: localInputToMs(dueDate),
        });
        onSuccess(updated);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save project');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="modal-overlay z-[9999]">
      <div
        ref={modalRef}
        className="modal-panel max-w-xl"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-glass-400 hover:text-white p-2 touch-manipulation"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-semibold tracking-tight text-white mb-4 flex items-center gap-2">
          {mode === 'create' ? (
            <>
              <Briefcase className="w-6 h-6 text-accent-400" />
              New project
            </>
          ) : (
            <>
              <Pencil className="w-6 h-6 text-accent-400" />
              Edit project
            </>
          )}
        </h2>

        {error && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-sm text-red-300">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-sm text-glass-300 mb-1 block">Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="glass-input w-full text-sm"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="glass-input w-full text-sm"
              rows={3}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {mode === 'create' && (
              <div>
                <label className="text-sm text-glass-300 mb-1 block">Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as EventProjectStatus)}
                  className="glass-input w-full text-sm"
                >
                  {EVENT_PROJECT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {formatLabel(s)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Priority (1–10)</label>
              <select
                value={priority}
                onChange={(e) => setPriority(Number(e.target.value))}
                className="glass-input w-full text-sm"
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    P{p}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Category</label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="glass-input w-full text-sm"
                placeholder="e.g. seasonal"
              />
            </div>
          </div>

          <div>
            <label className="text-sm text-glass-300 mb-1 block">Due date</label>
            <input
              type="datetime-local"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="glass-input w-full text-sm"
            />
          </div>

          {mode === 'create' && tags.length > 0 && (
            <div>
              <label className="text-sm text-glass-300 mb-1 block">Tags</label>
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() =>
                        setSelectedTagIds((prev) =>
                          selected ? prev.filter((id) => id !== tag.id) : [...prev, tag.id],
                        )
                      }
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-medium border transition-all',
                        selected ? 'ring-1 ring-current' : 'opacity-60 hover:opacity-100',
                      )}
                      style={{
                        backgroundColor: `${tag.color}20`,
                        borderColor: `${tag.color}40`,
                        color: tag.color,
                      }}
                    >
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: tag.color }} />
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="glass-button-secondary text-sm">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !title.trim()}
              className="glass-button text-sm inline-flex items-center gap-1.5"
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
              {mode === 'create' ? 'Create project' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
